<script lang="ts">
  import { FREE_SKU, LEVELING_ADVANCED_GRANT, TIER_PRO_SKU } from "@colossus/contracts";
  import { m } from "#messages";
  import { demoHref } from "#lib/demo/links.ts";
  import EntranceFooter from "#lib/entrance/EntranceFooter.svelte";
  import EntranceHeader from "#lib/entrance/EntranceHeader.svelte";
  import { count } from "#lib/format.ts";
  import { guidePath } from "#lib/guide/pages.ts";
  import { getLocale } from "#lib/locale.ts";
  import { directImportBots } from "#lib/products/direct-import.ts";
  import type { ProductQuestion } from "#lib/products/feature.ts";
  import { planBadge } from "#lib/products/plan-badge.ts";
  import ProductHero from "#lib/products/ProductHero.svelte";
  import ProductSections from "#lib/products/ProductSections.svelte";
  import type { ProductSection } from "#lib/products/ProductSections.svelte";
  import { rulesOf } from "#lib/products/tiers.ts";
  import Meta from "#lib/seo/Meta.svelte";
  import CtaBand from "#lib/site/CtaBand.svelte";
  import DemoShowroom from "#lib/site/DemoShowroom.svelte";
  import Faq from "#lib/site/Faq.svelte";
  import { ENTRANCE_FRAME } from "#lib/ui/recipes.ts";
  import type { PageProps } from "./$types.ts";

  let { data }: PageProps = $props();

  const SECTIONS: readonly ProductSection[] = $derived([
    {
      id: "levels",
      title: m.pricing_compare_group_levels(),
      lede: m.products_group_levels_lede(),
      tiles: [
        {
          preview: "earning",
          title: m.landing_tile_levels_title(),
          body: m.landing_tile_levels_body(),
          plan: planBadge(null),
        },
        {
          preview: "rules",
          title: m.pricing_compare_leveling_rules(),
          body: m.products_row_rules_body(),
          plan: planBadge(LEVELING_ADVANCED_GRANT),
        },
        {
          preview: "climb",
          title: m.pricing_compare_leveling_curve(),
          body: m.products_leveling_curve_body(),
          plan: planBadge(LEVELING_ADVANCED_GRANT),
        },
        {
          preview: "import",
          title: m.features_import(),
          body: m.features_import_body({ bots: directImportBots(getLocale()) }),
          plan: planBadge(null),
        },
      ],
    },
    {
      id: "rewards",
      title: m.features_group_rewards(),
      lede: m.products_group_rewards_lede(),
      tiles: [
        {
          preview: "ladder",
          title: m.features_role_rewards(),
          body: m.features_role_rewards_body(),
          plan: planBadge(null),
        },
        {
          preview: "perks",
          title: m.features_achievements(),
          body: m.features_achievements_body(),
          plan: planBadge(LEVELING_ADVANCED_GRANT),
        },
        {
          preview: "boosts",
          title: m.features_perk_groups(),
          body: m.features_perk_groups_body(),
          plan: planBadge(LEVELING_ADVANCED_GRANT),
        },
        {
          preview: "requests",
          title: m.features_personal_roles(),
          body: m.features_personal_roles_body(),
          plan: planBadge(LEVELING_ADVANCED_GRANT),
        },
      ],
    },
    {
      id: "level-ups",
      title: m.pricing_compare_group_levelups(),
      lede: m.products_group_level_ups_lede(),
      tiles: [
        {
          preview: "level-up",
          title: m.features_level_ups(),
          body: m.features_level_ups_body(),
          plan: planBadge(null),
        },
        {
          preview: "rank",
          title: m.features_rank_command(),
          body: m.features_rank_command_body(),
          plan: planBadge(null),
        },
        {
          preview: "editor",
          title: m.features_cards(),
          body: m.features_cards_body(),
          plan: planBadge(null),
        },
      ],
    },
  ]);

  const QUESTIONS: readonly ProductQuestion[] = $derived([
    {
      question: m.products_leveling_faq_start_q(),
      answer: m.products_leveling_faq_start_a(),
      href: guidePath("leveling"),
    },
    {
      question: m.products_leveling_faq_bots_q(),
      answer: m.products_leveling_faq_bots_a({ bots: directImportBots(getLocale()) }),
      href: guidePath("import"),
    },
    {
      question: m.products_leveling_faq_beside_q(),
      answer: m.products_leveling_faq_beside_a(),
      href: guidePath("import"),
    },
    {
      question: m.products_leveling_faq_rules_q(),
      answer: m.products_leveling_faq_rules_a({
        free: count(getLocale(), rulesOf(FREE_SKU)),
        pro: count(getLocale(), rulesOf(TIER_PRO_SKU)),
      }),
      href: guidePath("xp-and-levels"),
    },
    {
      question: m.products_leveling_faq_lapse_q(),
      answer: m.products_leveling_faq_lapse_a(),
      href: guidePath("billing"),
    },
    {
      question: m.products_leveling_faq_optout_q(),
      answer: m.products_leveling_faq_optout_a(),
      href: `${guidePath("privacy")}#opt-out`,
    },
  ]);
</script>

<Meta title={m.meta_leveling_title()} description={m.meta_leveling_description()} />

<EntranceHeader me={data.me} />

<main class="overflow-x-clip">
  <ProductHero
    headline={m.products_leveling_headline()}
    lede={m.products_leveling_lede()}
    testId="leveling-hero"
  >
    {#snippet picture()}
      <DemoShowroom href={demoHref("levels")} testId="leveling-live" hero="levels" />
    {/snippet}
  </ProductHero>

  <ProductSections sections={SECTIONS} testId="leveling" />

  <div class={[ENTRANCE_FRAME, "pt-28 sm:pt-36"]}>
    <Faq title={m.products_faq_title()} questions={QUESTIONS} testId="leveling-faq" />
  </div>

  <CtaBand title={m.products_closing_title()} body={m.products_closing_body()} />
</main>

<EntranceFooter />
